import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import {
  AppButton,
  StickyHeaderNavigation,
  OTPInput,
} from '@/components'
import { useVerifyEmailUpdate } from '@/domain'
import { useProfileUpdateStore } from '@/store/profileUpdate'

export const NewEmailValidateCodeScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const { newEmail } = useProfileUpdateStore()

  const { verifyEmailUpdate, isLoading } = useVerifyEmailUpdate({
    onSuccess: () => {
      // Navigate to success page
      navigate('/profile/profile-settings', {
        state: { message: t('screens:editEmail.emailUpdated') },
      })
    },
    onError: ({ errorMessage }) => {
      setErrorMessage(errorMessage || t('api:validationErrors.invalidCode'))
    },
  })

  const handleSubmit = () => {
    if (code.length === 6) {
      setErrorMessage('')
      verifyEmailUpdate({
        code,
        email: newEmail || '',
        new_email: true,
      })
    }
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  return (
    <BaseView flex={1} bgColor="background.screen" className="h-full">
      {/* Fixed Header Navigation */}
      <StickyHeaderNavigation
        title={t('screens:editEmail.verifyNewEmail')}
        onBackPress={handleGoBack}
      />

      {/* Scrollable Content */}
      <BaseView
        flex={1}
        px={12}
        style={{
          overflowY: 'auto',
        }}
      >
        <BaseView gap={24}>
          <BaseView gap={8}>
            <BaseText fontSize={18} fontFamily="semibold" color="label.default">
              {t('screens:editEmail.enterVerificationCode')}
            </BaseText>
            <BaseText
              fontSize={14}
              fontFamily="regular"
              color="label.description"
            >
              {t('screens:editEmail.verificationCodeSentTo', {
                email: newEmail,
              })}
            </BaseText>
          </BaseView>

          <OTPInput
            value={code}
            onChange={setCode}
            errorMessage={errorMessage}
            length={6}
          />

          {errorMessage && (
            <BaseText
              fontSize={14}
              fontFamily="regular"
              color="label.textInputError"
            >
              {errorMessage}
            </BaseText>
          )}
        </BaseView>

        {/* Footer Button */}
        <BaseView
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '24px 12px',
            backgroundColor: 'var(--background-screen)',
            borderTop: '1px solid var(--background-card-item-root)',
          }}
        >
          <AppButton
            label={t('form:actions.verify')}
            onClick={handleSubmit}
            disabled={code.length !== 6}
            isLoading={isLoading}
          />
        </BaseView>
      </BaseView>
    </BaseView>
  )
}
